import { useOrganizationsIdsQuery } from "@/graphql"
import NotFound from "@/pages/not-found/NotFound"
import WebsiteLandingLoader from "@/pages/website-landing/components/website-landing-loader/WebsiteLandingLoader"
import { ROUTES } from "@/router/routes"
import { EASETECH } from "@/utilities/global-variables"
import { message } from "antd"
import { FC, ReactNode } from "react"
import { useLocation } from "react-router"
import { useParams } from "react-router-dom"

const { website } = ROUTES

type CheckOrgExistenceProps = {
  children: ReactNode
}

const CheckOrgExistence: FC<CheckOrgExistenceProps> = ({ children }) => {
  const { org } = useParams()
  const location = useLocation()
  // The platform owner always exists: its marketing pages render without waiting for the API, so a slow or failed
  // request can never show search engines "Not found" on them.
  const isEasetech = org?.toLowerCase() === EASETECH
  const { data, loading } = useOrganizationsIdsQuery({
    variables: {
      filter: { slug: { eqi: org } },
    },
    skip: isEasetech,
    onError: error => message.error(error.message),
  })

  const currentPageName = location.pathname.split("/")[2]
  const isOrgHomePage = currentPageName === website.home
  // NOTE: isHerotelAoiPage is a deliberate remaining exception. CheckOrgExistence runs before
  // FeatureProvider can resolve, so we cannot use useFeature("aoi") here without a loading flash.
  // Once Phase 4 server-side enforcement lands, this slug check becomes redundant for protection;
  // it only gates the public landing route. Review and remove in Phase 7 cleanup.
  const isHerotelAoiPage = org === "herotel" && currentPageName === website.aoi

  if (isEasetech) {
    return <>{children}</>
  }

  if (loading) {
    return <WebsiteLandingLoader />
  }

  if (!data?.organizations?.data?.length) {
    return <NotFound mode={"org"} />
  }

  if (currentPageName && !isOrgHomePage && !isHerotelAoiPage) {
    return <NotFound mode={"page"} />
  }

  return <>{children}</>
}

export default CheckOrgExistence
